import React from 'react'
import { AlertTriangle, CheckCircle, Clock, Eye } from 'lucide-react'
import type { RiskAlert } from '@/types/risk-assessment'

interface AlertsSummaryProps {
  alerts: RiskAlert[]
}

export function AlertsSummary({ alerts }: AlertsSummaryProps) {
  const unacknowledgedAlerts = alerts.filter(alert => !alert.acknowledged)
  const criticalAlerts = unacknowledgedAlerts.filter(alert => alert.severity === 'critical')
  const highAlerts = unacknowledgedAlerts.filter(alert => alert.severity === 'high')
  const totalAffectedProperties = new Set(
    unacknowledgedAlerts.flatMap(alert => alert.affected_properties)
  ).size

  if (alerts.length === 0) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-center space-x-3">
          <CheckCircle className="w-5 h-5 text-green-600" />
          <div>
            <h4 className="font-medium text-green-800">No Active Alerts</h4>
            <p className="text-sm text-green-600">Your portfolio is within acceptable risk parameters.</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {/* Total Alerts */}
      <div className="bg-white border rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">Active Alerts</p>
            <p className="text-2xl font-bold text-gray-900">{unacknowledgedAlerts.length}</p>
          </div>
          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
            <AlertTriangle className="w-4 h-4 text-blue-600" />
          </div>
        </div>
      </div>

      {/* Critical Alerts */}
      <div className="bg-white border rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">Critical</p>
            <p className="text-2xl font-bold text-red-600">{criticalAlerts.length}</p>
          </div>
          <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
            <AlertTriangle className="w-4 h-4 text-red-600" />
          </div>
        </div>
        {criticalAlerts.length > 0 && (
          <div className="mt-2">
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
              Immediate Action Required
            </span>
          </div>
        )}
      </div>

      {/* High Priority Alerts */}
      <div className="bg-white border rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">High Priority</p>
            <p className="text-2xl font-bold text-orange-600">{highAlerts.length}</p>
          </div>
          <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
            <Clock className="w-4 h-4 text-orange-600" />
          </div>
        </div>
      </div>

      {/* Affected Properties */}
      <div className="bg-white border rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">Properties at Risk</p>
            <p className="text-2xl font-bold text-gray-900">{totalAffectedProperties}</p>
          </div>
          <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
            <Eye className="w-4 h-4 text-gray-600" />
          </div>
        </div>
        {totalAffectedProperties > 0 && (
          <div className="mt-2">
            <p className="text-xs text-gray-500">
              {((totalAffectedProperties / (alerts.length > 0 ? Math.max(...alerts.map(a => a.affected_properties.length)) : 1)) * 100).toFixed(0)}% of portfolio
            </p>
          </div>
        )}
      </div>
    </div>
  )
}